import React, { useMemo, useState } from 'react';
import cn from 'classnames';

import {
	Button,
	Checkbox,
	Confirm,
	DynamicIconWrapper,
	ExportActionsTypes,
	FileTypeSelectionCommonForm,
	Icons,
	Modal,
	SliderCheckbox,
} from '@components/common';

import { kpi } from '@store/selectors';
import { useAppDispatch, useAppSelector } from '@store/store';

import { setKpiSourceClickedState } from '@slices/kpi';
import {
	setActiveCustomPolygons,
	setActiveSiteMarkers,
	setSelectedCustomPolygonForView,
} from '@slices/map';

import { checkIsEmptyChart } from '@utils/kpi-utils/chart';

import { IExportKpiDTO } from '@interfaces/kpi/dto/kpiDTO';
import { IExportFileDTO } from '@interfaces/platform/dto/platformDashboardDTO';
import { ICommonControlPanelTypes } from './CommonControlPanel.types';

import { EVendorsValues } from '@enums/common';

import { exportRadioButtonsData } from '@constants/common';
import { MODAL_WIDTH_455 } from '@constants/modals';

import './CommonControlPanel.scss';

const CommonControlPanel = ({
	allCharts,
	exportCharts,
	isHideEmptyCharts,
	isMap,
	selectedIds,
	setAllCharts,
	setChangedSizeCharts,
	setDeletedChartIds,
	setSelectedIds,
	setVisibleThresholdIds,
	toggleHideEmptyCharts,
	visibleThresholdIds,
}: ICommonControlPanelTypes) => {
	const dispatch = useAppDispatch();

	const isOranVendor = allCharts.some(chart => chart.vendor.name === EVendorsValues.oran);

	const {
		activeCharts,
		lastKpiSourceData,
		chartsReceivedAt,
	} = useAppSelector(kpi);

	const [isOpenDeleteConfirmCards, setIsOpenDeleteConfirmCards] = useState(false);
	const [isOpenFileTypeSelectionModal, setIsOpenFileTypeSelectionModal] = useState(false);

	const countOfCharts = allCharts.length;
	const availableThresholdIds = allCharts.reduce<Array<string>>((acc, chart) => {
		const isEmptyChart = checkIsEmptyChart(chart);
		const thresholds = chart.thresholds;

		if (!isEmptyChart && thresholds.length) acc.push(chart.chartId);

		return acc;
	}, []);

	const isSelectAll = countOfCharts === selectedIds.length;
	const isVisibleAllThresholds = availableThresholdIds.length === visibleThresholdIds.length;
	const isAllSelectedChartsEmpty = useMemo(() => !allCharts.filter(chart => {
		const isEmptyChart = checkIsEmptyChart(chart);

		return selectedIds.includes(chart.chartId) && !isEmptyChart;
	}).length, [selectedIds, allCharts]);
	const toggleDeleteConfirm = () => {
		if (!selectedIds.length) return;

		setIsOpenDeleteConfirmCards(prevState => !prevState);
	};

	const onChangeSelectAll = () => {
		if (isSelectAll || selectedIds.length) setSelectedIds([]);
		else setSelectedIds(allCharts.map((chart) => chart.chartId));
	};

	const onChangeVisibleAllThresholds = () => {
		if (isVisibleAllThresholds || visibleThresholdIds.length) setVisibleThresholdIds([]);
		else setVisibleThresholdIds(availableThresholdIds);
	};

	const onDeleteHandler = () => {
		if (!selectedIds.length) return;

		setAllCharts(prevState => prevState.filter(chart => !selectedIds.includes(chart.chartId)));
		setDeletedChartIds(prevState => ([...prevState, ...selectedIds]));
		setSelectedIds([]);
		toggleDeleteConfirm();
	};

	const onCollapseCards = () => {
		if (!selectedIds.length) return;

		setAllCharts(prevState => prevState.map(chart => selectedIds.includes(chart.chartId) ? ({
				...chart,
				isOpen: false,
			}) : chart,
		));

		setChangedSizeCharts({ ids: selectedIds, isOpen: false });
	};

	const onExpandCards = () => {
		if (!selectedIds.length) return;

		setAllCharts(prevState => prevState.map(chart => selectedIds.includes(chart.chartId) ? ({
				...chart,
				isOpen: true,
			}) : chart,
		));

		setChangedSizeCharts({ ids: selectedIds, isOpen: true });
	};

	const toggleFileTypeSelectionModal = () => setIsOpenFileTypeSelectionModal(prevState => !prevState);

	const closeTypeSelectionForm = () => setIsOpenFileTypeSelectionModal(false);

	const exportHandler = (data: IExportFileDTO) => {
		const charts = allCharts.filter(chart => {
			const isEmptyChart = checkIsEmptyChart(chart);

			return selectedIds.includes(chart.chartId) && !isEmptyChart;
		});

		if (!charts.length || !chartsReceivedAt) return;

		const exportData: Array<IExportKpiDTO> = charts.map(chart => {
			const preparedTimeAggregationType = chart.yaxisAggregationType.name;
			const granularity = isOranVendor ? chart.granularity?.name : undefined;

			return ({
				fileType: data.fileType,
				aggregationType: chart.aggregationType.name,
				from: chart.from,
				to: chart.to,
				lastSeenTime: chartsReceivedAt,
				kpiIds: [chart.kpiId],
				sites: chart.sites,
				timeAggregationType: preparedTimeAggregationType,
				vendor: chart.vendor.name,
				granularity,
			});
		});

		exportCharts(exportData);
	};

	const onChangeHideEmpty = (_: string, checked: boolean) => toggleHideEmptyCharts(checked);

	const thresholdsElement = (<div className='f5gCommonControlPanelThresholds'>
		<Checkbox
			checked={isVisibleAllThresholds && !!availableThresholdIds.length}
			disabled={!availableThresholdIds.length}
			isIndefinite={!!visibleThresholdIds.length && !isVisibleAllThresholds}
			label='Threshold'
			name='threshold-all-checkbox'
			onChange={onChangeVisibleAllThresholds}
		/>
	</div>);

	const showKpiSource = () => {
		if (lastKpiSourceData) {
			dispatch(setKpiSourceClickedState(true));
			dispatch(setSelectedCustomPolygonForView(lastKpiSourceData.selectedPolygonIds[0]));
			dispatch(setActiveSiteMarkers(lastKpiSourceData.selectedSiteIds));
			dispatch(setActiveCustomPolygons(lastKpiSourceData.selectedPolygonIds));
		}
	};

	return (
		<>
			<Modal
				isOpen={isOpenFileTypeSelectionModal}
				onClose={toggleFileTypeSelectionModal}
				title='File extension to export'
				width={MODAL_WIDTH_455}
			>
				<FileTypeSelectionCommonForm
					activeExportAction={ExportActionsTypes.all}
					closeModals={closeTypeSelectionForm}
					exportRadioButtonsData={exportRadioButtonsData}
					handlePreparingExport={exportHandler}
					isLoading={false}
					toggleModal={toggleFileTypeSelectionModal}
				/>
			</Modal>
			<Modal
				isOpen={isOpenDeleteConfirmCards}
				onClose={toggleDeleteConfirm}
			>
				<Confirm
					onClose={toggleDeleteConfirm}
					onSubmit={onDeleteHandler}
					question='Are you sure you want to remove selected chart(s)?'
				/>
			</Modal>
			<div className={cn('f5gCommonControlPanelContainer')}>
				{isMap &&
					<div className='f5gCommonControlPanelHeader'>
						<div className='f5gCommonControlPanelMapTitle'>
							KPI charts
						</div>
						{!!activeCharts?.length
							&& <Button
								onClick={showKpiSource}
								secondary
								text='Show KPI source'
							/>
						}
					</div>
				}
				<div
					className={cn('f5gCommonControlPanelFlexWrapperOnDashboard', { 'f5gCommonControlPanelFlexWrapperOnMap': isMap })}>
					<div className={cn({ 'f5gCommonControlPanelCheckboxesOnMap': isMap })}>
						<Checkbox
							checked={isSelectAll && !!allCharts.length}
							disabled={!allCharts.length}
							isIndefinite={!!selectedIds.length && !isSelectAll}
							label='Select all'
							name='select-all'
							onChange={onChangeSelectAll}
						/>
						{isMap && thresholdsElement}
					</div>
					<div className='f5gCommonControlPanelFilters'>
						<DynamicIconWrapper
							icon={Icons.export}
							isDisabled={isAllSelectedChartsEmpty}
							onClick={toggleFileTypeSelectionModal}
							tooltipLabel='Export selected'
						/>
						<div onClick={toggleDeleteConfirm}>
							<DynamicIconWrapper
								icon={Icons.delete}
								isDisabled={!selectedIds.length}
								tooltipLabel='Remove selected'
							/>
						</div>
						<div
							className='f5gCommonControlPanelArrow'
							onClick={onExpandCards}
						>
							<DynamicIconWrapper
								icon={Icons.chevronDown}
								isDisabled={!selectedIds.length}
								tooltipLabel='Expand selected'
							/>
						</div>
						<div
							className='f5gCommonControlPanelArrow'
							onClick={onCollapseCards}
						>
							<DynamicIconWrapper
								icon={Icons.chevronUp}
								isDisabled={!selectedIds.length}
								tooltipLabel='Collapse selected'
							/>
						</div>
						{!isMap && thresholdsElement}
					</div>
				</div>
				<div className='f5gCommonControlPanelHideEmptySlider'>
					<SliderCheckbox
						checked={isHideEmptyCharts}
						id='hide-empty-charts'
						isLabelAfter
						label='Hide empty charts'
						onChange={onChangeHideEmpty}
					/>
				</div>
			</div>
		</>
	);
};

export default CommonControlPanel;
